import { DepositWithdrawalRequestFilters } from '@/XplorPortal/PortalDeposit';
import { DepositWithdrawalRequestHistoryReturn } from '@/types/sso';
import useContextMenu from '@/utils/hooks/useContextMenu';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalWithdrawalTable from '@/utils/hooks/usePortalWithdrawalTable';
import { useTableDisclosure } from '@/utils/hooks/useTableDisclosure';
import { useSso } from '@/utils/providers/SSOProvider';
import { Dispatch, useCallback, useMemo, useState } from 'react';
import { useDateFormatter } from 'react-aria';
import CurrentTradeDate from '../common/CurrentTradeDate';
import PortalVirtualizedTable, { PortalTableGeneralProps } from '../table/PortalVirtualizedTable';
import TableControls from '../table/TableControls';
import TablePagination from '../table/TablePagination';
import { DepositWithdrawalRequestTableFilters } from '../table/filters/DepositWithdrawalRequestTableFilters';

export interface PortalWithdrawalRow {}

export interface PortalWithdrawalTableProps extends PortalTableGeneralProps {
    data?: DepositWithdrawalRequestHistoryReturn;
    filters: DepositWithdrawalRequestFilters;
    pagination: { pageSize: number };
    setPagination: Dispatch<React.SetStateAction<{ pageSize: number }>>;
    setFilters: (filters: DepositWithdrawalRequestFilters) => void;
    setOffset: (offset: number) => void;
}

export type PositionFilter = 'Show All' | 'Hide Closed Positions';

const PortalWithdrawalTable = (props: PortalWithdrawalTableProps) => {
    const { filters, pagination, isLoading, setPagination } = props;
    const { startDate, endDate } = filters;

    const { foundEntity } = useSso();

    const lang = useLanguage();
    const [selectedRow, setSelectedRow] = useState<PortalWithdrawalRow>();

    const contextMenu = useContextMenu();
    const dateFormatterForExcel = useDateFormatter();

    const { filterDisclosure, viewDisclosure } = useTableDisclosure({
        setSelectedRow
    });

    const onContextMenu = useCallback(
        (event, request) => {
            setSelectedRow(request);
            contextMenu.handleOpenContextMenu(event);
        },
        [contextMenu]
    );

    const { formattedPortalWithdrawals, debouncedTableInstance, globalFilter } = usePortalWithdrawalTable(props);

    const tableHeaders = useMemo(() => {
        return debouncedTableInstance.getLeafHeaders().map((header) => ({
            id: header.id,
            label: header.column.columnDef.header
        }));
    }, [debouncedTableInstance, isLoading]);

    const formatDataForExcel = useCallback(
        (data) => {
            return data.map((data) => {
                const formattedData = tableHeaders.map((header) => {
                    switch (header.id) {
                        case 'referredClient':
                            return data.referredClient || '-';
                        default:
                            return data[header.id];
                    }
                });

                return formattedData;
            });
        },
        [tableHeaders]
    );

    const fileName = useMemo(() => {
        return `Deposit-${foundEntity?.entityName.split(' ').join('-')}-${dateFormatterForExcel.format(
            new Date(startDate.value)
        )}-${dateFormatterForExcel.format(new Date(endDate.value))}`;
    }, [foundEntity, startDate, endDate, dateFormatterForExcel]);

    const excelData = useMemo(() => {
        return {
            data: formatDataForExcel(formattedPortalWithdrawals),
            fileName,
            headerData: tableHeaders.map((header) => header.label)
        };
    }, [formattedPortalWithdrawals, fileName, tableHeaders]);

    return (
        <div className="flex flex-col gap-1 bg-brand-background-dark p-4 sm:p-6 py-4 border border-brand-neutral-700 text-brand-text rounded-md overflow-hidden max-h-[600px]">
            <div className="flex flex-col h-full gap-4">
                <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center flex-wrap gap-2 sm:gap-5">
                        <div className="">{lang.portal.withdrawalHistory}</div>
                        <TableControls
                            excelData={excelData}
                            filterDisclosure={filterDisclosure}
                            mutate={props.mutate}
                            isLoading={props.isLoading}
                        />
                    </div>
                    <DepositWithdrawalRequestTableFilters
                        {...props}
                        opened={filterDisclosure[0]}
                        globalFilter={globalFilter}
                        setGlobalFilter={debouncedTableInstance.setGlobalFilter}
                        resetPageNumber={debouncedTableInstance.setPageIndex}
                    />
                </div>
                <PortalVirtualizedTable tableInstance={debouncedTableInstance} isLoadingTableContent={isLoading} />
                <div className="flex flex-wrap-reverse gap-2 items-center justify-between">
                    <CurrentTradeDate />
                    <TablePagination
                        tableInstance={debouncedTableInstance}
                        manualPagination={pagination}
                        setManualPagination={setPagination}
                    />
                </div>
            </div>
        </div>
    );
};

export default PortalWithdrawalTable;
